import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

const SBL_LOGO = "/sbl-logo.jpeg";

export default function ChangePasswordPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (currentPassword === newPassword) {
      setError("New password must be different from current password");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully!");
      // Update user state to remove must_change_password flag
      setUser((prev) => (prev ? { ...prev, must_change_password: false } : prev));
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to change password");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm bg-white border border-slate-200 shadow-sm p-8">
        <div className="flex items-center gap-2 mb-6">
          <img src={SBL_LOGO} alt="SBL" className="w-10 h-10 object-contain rounded-sm" />
          <span className="text-xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
            School Builder Lab Africa
          </span>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <LockKey size={24} className="text-blue-800" weight="fill" />
          <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Change Your Password
          </h2>
        </div>

        <p className="text-sm text-slate-500 mb-6">
          {user?.must_change_password
            ? "Your account requires a password change before you can continue."
            : "Update your password for security."}
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 mb-4" data-testid="change-pw-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="current-password" className="text-slate-700 text-sm font-medium">Current Password</Label>
            <Input
              id="current-password"
              type="password"
              placeholder="Enter current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              data-testid="current-password-input"
              className="mt-1.5 rounded-none border-slate-300 focus:border-blue-800 focus:ring-1 focus:ring-blue-800"
            />
          </div>
          <div>
            <Label htmlFor="new-password" className="text-slate-700 text-sm font-medium">New Password</Label>
            <Input
              id="new-password"
              type="password"
              placeholder="Min 6 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              data-testid="new-password-input"
              className="mt-1.5 rounded-none border-slate-300 focus:border-blue-800 focus:ring-1 focus:ring-blue-800"
            />
          </div>
          <div>
            <Label htmlFor="confirm-password" className="text-slate-700 text-sm font-medium">Confirm New Password</Label>
            <Input
              id="confirm-password"
              type="password"
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              data-testid="confirm-password-input"
              className="mt-1.5 rounded-none border-slate-300 focus:border-blue-800 focus:ring-1 focus:ring-blue-800"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="change-password-submit-btn"
            className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none font-medium h-11 transition-all active:scale-[0.98]"
          >
            {loading ? "Changing..." : "Change Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
